/**
 * video-hover-media
 *
 * A lightweight custom element for product card hover videos.
 * Unlike <video-media autoplay>, this element:
 *   - Does NOT play on viewport entry (no IntersectionObserver)
 *   - Plays only when the parent product card is hovered
 *   - Pauses and resets on mouse leave
 *   - Defers loading video bytes until the first hover (true lazy load)
 *   - Only activates on fine-pointer (mouse) devices
 */
class VideoHoverMedia extends HTMLElement {
  connectedCallback() {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    this._abortController = new AbortController();
    const signal = this._abortController.signal;
    this._sourcesLoaded = false;

    const video = this.querySelector('video');
    if (!video) return;

    // Defer all video bytes: move src → data-src and set preload="none"
    video.querySelectorAll('source').forEach((source) => {
      if (source.getAttribute('src')) {
        source.dataset.src = source.getAttribute('src');
        source.removeAttribute('src');
      }
    });
    video.preload = 'none';
    video.load(); // reset any partial fetch already triggered by the browser

    // Attach hover listeners to the product card figure for a larger hit area
    const figure = this.closest('.product-card__figure') || this;
    figure.addEventListener('mouseenter', this._onEnter.bind(this), { signal });
    figure.addEventListener('mouseleave', this._onLeave.bind(this), { signal });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  _onEnter() {
    const video = this.querySelector('video');
    if (!video) return;

    if (!this._sourcesLoaded) {
      // Restore sources on first hover, then trigger load + play
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.dataset.src);
      });
      this._sourcesLoaded = true;
      video.load();
    }

    video.play().catch(() => {
      // Autoplay blocked — browser will show the poster/preview image instead
    });
  }

  _onLeave() {
    const video = this.querySelector('video');
    if (!video) return;
    video.pause();
  }
}

if (!window.customElements.get('video-hover-media')) {
  window.customElements.define('video-hover-media', VideoHoverMedia);
}
